import { Subscription, combineLatest } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { LoginStatusType } from '@lib/interfaces';
import { CommonLibService } from '@lib/services';

import { FormGroup } from '@angular/forms';
import { SupportComponent } from '@lib/components/support-class';
import { ServiceRequestPaymentInputService } from '@common/services/service-request-payment-input/service-request-payment-input-service';
import { GetOrderResponseData } from 'src/sdk-servicing';
import { GetBasicReservationInformationResponseData } from 'src/sdk-amcmember';
import {
  addNotificationEmails,
  updateConfirmEmailValidator,
} from '@app/service-request-payment-input/presenter/service-request-payment-input-pres.form';

@Component({
  selector: 'asw-service-request-payment-input-notification',
  templateUrl: './service-request-payment-input-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceRequestPaymentInputNotificationComponent extends SupportComponent {
  _subscriptions: Subscription = new Subscription();

  public mailDestination!: number[];

  public pnrData!: GetOrderResponseData;

  public amcData?: GetBasicReservationInformationResponseData;

  public passengerEmailDestinationHeading: string = '';

  public passengerSmsDestinationHeading: string = '';

  get paymentInputFG(): FormGroup {
    return this._serviceRequestPaymentInputService.paymentInputFG;
  }

  constructor(
    private readonly _serviceRequestPaymentInputService: ServiceRequestPaymentInputService,
    private readonly _common: CommonLibService,
    private readonly _changeDetectorRef: ChangeDetectorRef
  ) {
    super(_common);
  }

  init(): void {
    this._subscriptions.add(
      combineLatest([
        this._serviceRequestPaymentInputService.pnr$,
        this._serviceRequestPaymentInputService.amc$,
      ]).subscribe(([pnr, amc]) => {
        this.pnrData = pnr;
        this.amcData = amc;
        addNotificationEmails(this.paymentInputFG);
        this.getConfirmationEmail();
        this.getConfirmationEmailConfirm();
        this.getPassengerEmailDestinationHeading();
        this.getPassengerSmsDestinationHeading();
      })
    );
    this._subscriptions.add(
      this._serviceRequestPaymentInputService.checkValid$.subscribe((checkValid) => {
        if (checkValid) {
          this._changeDetectorRef.detectChanges();
        }
      })
    );
  }

  getConfirmationEmail() {
    // [8.1] 申込完了連絡先メールアドレス
    // 以下に示す順序に従い、最初に合致するものを初期表示する。
    let comfirmationEmail: string;
    // [8.1.1] ユーザ共通情報.ログインステータス=”REAL_LOGIN""(ログイン済み)の場合、予約基本情報取得API応答.data.priorEmailを表示する。
    if (
      this._common.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.REAL_LOGIN &&
      this.amcData?.priorEmail
    ) {
      comfirmationEmail = this.amcData?.priorEmail;
    }
    // [8.1.2] PNR情報取得API応答.data.contacts.representative.emails[0].address が存在するの場合、
    // PNR情報取得API応答.data.contacts.representative.emails[0].addressを表示する
    else if (this.pnrData.contacts?.representative?.emails?.[0]?.address) {
      comfirmationEmail = this.pnrData.contacts.representative.emails[0].address;
    }
    // [8.1.3] 上記以外の場合、””(空欄)を表示する。
    else {
      comfirmationEmail = '';
    }
    this.paymentInputFG?.get('notificationConfirmationEmail')?.setValue(comfirmationEmail);
  }
  getConfirmationEmailConfirm() {
    // [8.2]  申込完了連絡先メールアドレス(確認用)
    // 初期表示は以下に示す順序に従い、最初に合致するものを初期表示する。
    let comfirmationConfirmEmail: string;
    // [8.2.1] ユーザ共通情報.ログインステータス=”REAL_LOGIN""(ログイン済み)の場合、予約基本情報取得API応答.data.priorEmailを表示する。
    if (this._common.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.REAL_LOGIN) {
      comfirmationConfirmEmail = this.amcData?.priorEmail ?? '';
    }
    // [8.2.2] PNR情報取得API応答.data.contacts.representative.emails[0].address が存在するの場合、PNR情報取得API応答.data.contacts.representative.emails[0].addressを表示する。
    else if (this.pnrData.contacts?.representative?.emails?.[0]?.address) {
      comfirmationConfirmEmail = this.pnrData.contacts.representative.emails[0].address;
    }
    // [8.2.3] 上記以外の場合、””(空欄)を表示する。
    else {
      comfirmationConfirmEmail = '';
    }
    this.paymentInputFG.get('notificationConfirmationConfirmEmail')?.setValue(comfirmationConfirmEmail);
  }

  getPassengerEmailDestinationHeading() {
    // [8.3] 搭乗者メール送信先の見出し
    // [8.3.1] NHグループ運航でアメリカ行き便有無=trueの場合、用途として入国管理にて利用される旨を含む文言を表示する。
    let isNhGroupOperatedAndArrivalUs = this._serviceRequestPaymentInputService.isNhGroupOperatedAndArrivalUs;

    if (isNhGroupOperatedAndArrivalUs) {
      this.passengerEmailDestinationHeading = 'heading.passengerEmailForEnteringUs';
    }
    // [8.3.2] 記以外の場合、用途として入国管理にて利用されることが含まれない文言を表示する
    else {
      this.passengerEmailDestinationHeading = 'message.recipientOfMailAboutBoardingForServiceRequest';
    }
  }

  getPassengerSmsDestinationHeading() {
    // [8.9] 搭乗者SMS送信先の見出し
    // [8.9.1] 1.PNR情報取得API応答.data.air.bounds.flightsの中に、isNhGroupOperated = true (NHグループ運航)、
    // かつarrival.country2letterCode = ""US""が存在する場合、
    // 用途として入国管理にて利用される旨を含む文言を表示する。
    let isNhGroupOperatedAndArrivalUs = this._serviceRequestPaymentInputService.isNhGroupOperatedAndArrivalUs;

    if (isNhGroupOperatedAndArrivalUs) {
      this.passengerSmsDestinationHeading = 'heading.passengerPhoneNumberForEnteringUs';
    }
    // [8.9.2] 上記以外の場合、用途として入国管理にて利用されることが含まれない文言を表示する。
    else {
      this.passengerSmsDestinationHeading = 'heading.recipientOfSmsAboutBoardingForServiceRequest1';
    }
  }

  // (14.1.1.2.2) 申込完了連絡先メールアドレスが存在する場合、申込完了連絡先メールアドレスと申込完了連絡先メールアドレス(確認用)が一致しない場合、E0461を表示する。
  emailChange() {
    updateConfirmEmailValidator(
      this.paymentInputFG,
      'notificationConfirmationEmail',
      'notificationConfirmationConfirmEmail'
    );
  }

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  reload(): void {
    /* override abstract reload function of base class */
  }
}
